/**
 * Theme mode (light / dark / auto) — Obsidian-style toggle.
 *
 * - Persisted in localStorage so the choice survives reloads
 * - "auto" follows the OS prefers-color-scheme
 * - Wrapped at the top of the FluentProvider tree (in AuthProvider)
 *
 * Use the `useThemeMode()` hook to read or change the current mode from
 * any descendant.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { type Theme } from '@fluentui/react-components';
import { prismLightTheme, prismDarkTheme } from './prismTheme';

export type ThemeMode = 'light' | 'dark' | 'auto';

const STORAGE_KEY = 'prism_theme_mode';

interface ThemeContextValue {
  mode: ThemeMode;
  setMode: (m: ThemeMode) => void;
  /** The actual Fluent theme object to pass to FluentProvider. */
  theme: Theme;
  /** What `auto` resolved to (light or dark) — useful for conditional css. */
  resolved: 'light' | 'dark';
}

const ThemeModeContext = createContext<ThemeContextValue>({
  mode: 'auto',
  setMode: () => {},
  theme: prismLightTheme,
  resolved: 'light',
});

export function useThemeMode() {
  return useContext(ThemeModeContext);
}

/** Provider — call once near the top of the app. */
export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'auto';
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved === 'light' || saved === 'dark' || saved === 'auto'
      ? saved
      : 'auto';
  });

  // Track OS preference for `auto` mode
  const [osPrefers, setOsPrefers] = useState<'light' | 'dark'>(() => {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) =>
      setOsPrefers(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const setMode = (next: ThemeMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* private mode etc. */
    }
  };

  const value = useMemo<ThemeContextValue>(() => {
    const resolved = mode === 'auto' ? osPrefers : mode;
    return {
      mode,
      setMode,
      resolved,
      theme: resolved === 'dark' ? prismDarkTheme : prismLightTheme,
    };
  }, [mode, osPrefers]);

  // Set a `data-theme` attribute on <html> so plain CSS can react
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.theme = value.resolved;
      document.documentElement.style.colorScheme = value.resolved;
    }
  }, [value.resolved]);

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
}
